import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import VariationsStyles from './VariationsStyles';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var25');

            if (variation.question) {
              const rightText = document.createElement('div');
              rightText.classList.add('question-var25');

              let text = '';
              const updatedText = replaceQuotes(variation.question);
              if (variation.quotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              const algorithmResult = getOptimalFontSize(text, {
                maxWidth: 900,
                maxHeight: 150,
                fontFamily: 'CustomArial',
                lineHeight: '1.2',
                letterSpacing: '0.8px',
                minFontSize: 45,
                maxFontSize: 65,
              });

              if (variation.brakes) {
                rightText.innerText = text;
              } else {
                rightText.innerHTML = algorithmResult.lines.join('<br/>');
              }

              if (variation.fontSize) {
                rightText.style.fontSize = `${variation.fontSize}px`;
              } else {
                rightText.style.fontSize = `${algorithmResult.fontSize}px`;
              }

              imageHolder.appendChild(rightText);
            }

            const answerA = document.createElement('div');
            if (variation.answerA) {
              answerA.classList.add('trivia-answer-var25', 'answer-a-var25');
              answerA.innerHTML = `<span>a)</span> ${variation.answerA}`;
            }

            const answerB = document.createElement('div');
            if (variation.answerB) {
              answerB.classList.add('trivia-answer-var25', 'answer-b-var25');
              answerB.innerHTML = `<span>b)</span> ${variation.answerB}`;
            }

            const answerC = document.createElement('div');
            if (variation.answerC) {
              answerC.classList.add('trivia-answer-var25', 'answer-c-var25');
              answerC.innerHTML = `<span>c)</span> ${variation.answerC}`;
            }

            const answerD = document.createElement('div');
            if (variation.answerD) {
              answerD.classList.add('trivia-answer-var25', 'answer-d-var25');
              answerD.innerHTML = `<span>d)</span> ${variation.answerD}`;
            }

            if (
              !variation.answerA &&
              !variation.answerB &&
              !variation.answerC &&
              !variation.answerD
            ) {
              imageHolder.style.justifyContent = 'center';
            }

            const bottomHolder = document.createElement('div');
            bottomHolder.classList.add('bottom-holder-var25');
            bottomHolder.appendChild(answerA);
            bottomHolder.appendChild(answerB);
            bottomHolder.appendChild(answerC);
            bottomHolder.appendChild(answerD);

            imageHolder.appendChild(bottomHolder);

            if (variation.answerFontSize) {
              answerA.style.fontSize = `${variation.answerFontSize}px`;
              answerB.style.fontSize = `${variation.answerFontSize}px`;
              answerC.style.fontSize = `${variation.answerFontSize}px`;
              answerD.style.fontSize = `${variation.answerFontSize}px`;
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var25',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      const validImages = await generateImagesInParallel(
        variations,
        generateImageVariations,
        (current) => {
          setCounter(current);
        },
      );

      setImages(validImages);
      setShowProgress(false);
      setCounter(0);
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages);
      } else {
        await downloadImagesAsZip(images);
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <VariationsStyles />
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
